import { useContext, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";

import backgroundChat from '../../assets/img/backgroundChat.png';

import TicketsManager from "../../components/TicketsManagerTabs/";
import Ticket from "../../components/Ticket/";

import { getSocket } from "../../services/socket";
import { useTicketRoom } from "../../hooks/useTicketRoom";

import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	chatContainer: {
		flex: 1,
		paddingLeft: theme.spacing(1),
		paddingTop: theme.spacing(3.5),
		height: `calc(100% - 78px)`,
		overflowY: "hidden",
		backgroundImage: `url(${backgroundChat})`,
		[theme.breakpoints.down(1044)]: {
			paddingTop: theme.spacing(0.25),
		}
	},

	chatPapper: {
		display: "flex",
		height: "100%",
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflowY: "hidden",
		boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px',
		borderRadius: '8px',
		[theme.breakpoints.down(1044)]: {
			width: '100%',
			flex: 1,
			display: props => props.ticketId ? 'none' : 'flex',
			flexBasis: '100%',
			maxWidth: '100%',
		}
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		[theme.breakpoints.down(1044)]: {
			display: props => props.ticketId ? 'flex' : 'none',
			flexBasis: '100%',
			maxWidth: '100%',
		}
	},
	welcomeMsg: {
		background: theme.palette.total,
		display: "flex",
		justifyContent: "space-evenly",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
		borderRadius: 15,
	},
	mobileWrapper: {
		width: '100%',
		height: '100%',
	},
	gridContainer: {
		[theme.breakpoints.down('md')]: {
			gridTemplateColumns: 'repeat(24, 1fr)',
		}
	},
	ticketManagerMd: {
		[theme.breakpoints.down('md')]: {
			flexBasis: props => props.ticketId ? '36%' : '100%',  
			maxWidth: props => props.ticketId ? '36%' : '100%',
		}
	},
	ticketMd: {
		[theme.breakpoints.down('md')]: {
			flexBasis: '64%', 
			maxWidth: '64%',
			display: props => props.ticketId ? 'block' : 'none'
		}
	}
}));

const TicketsCustom = () => {
	const { ticketId } = useParams();
	const history = useHistory();
	const classes = useStyles({ ticketId });
	const { ticketsList } = useContext(TicketsContext);
	const { user } = useContext(AuthContext);
	const ticketsListRef = useRef(ticketsList);
	ticketsListRef.current = ticketsList;

	// 🆕 Une/sale del room del ticket actual según el uuid del path
	useTicketRoom();

	// Efecto para manejar eventos de socket y detectar transferencias globales
	useEffect(() => {
		if (!ticketId) return;

		const companyId = localStorage.getItem("companyId");
		const socket = getSocket(companyId);

		const handleTicketUpdate = (data) => {
			// Buscar el ticket actual en la lista (ref: evita re-suscribir el socket en cada cambio de lista)
			const currentTicket = ticketsListRef.current.find(t => t.uuid === ticketId);
			if (!currentTicket) return;

			// Solo procesar eventos del ticket actual
        if (data.action === "update" && data.ticket && data.ticket.id === currentTicket.id) {
            const oldUserId = data?.metadata?.oldUserId;
            const oldStatus = data?.metadata?.oldStatus;
            console.log(`🔄 TicketsCustom - Ticket ${currentTicket.id} actualizado:`, {
                oldUserId,
                newUserId: data.ticket.userId,
                currentUserId: user.id,
                oldStatus,
                newStatus: data.ticket.status
            });

				// Verificar si el ticket fue transferido DESDE el usuario actual
            const wasAssignedToCurrentUser = oldUserId !== undefined && oldUserId === user.id;
				const isNowAssignedToOtherUser = data.ticket.userId !== user.id && data.ticket.userId !== null;
            const wasReturnedToPending = (oldUserId !== undefined && oldUserId === user.id) && data.ticket.userId === null && data.ticket.status === "pending";
            const wasClosedByOtherUser = data.ticket.status === "closed" && oldStatus !== "closed";

				if (wasAssignedToCurrentUser && (isNowAssignedToOtherUser || wasReturnedToPending)) {
					console.log(`🚪 TicketsCustom - Ticket ${currentTicket.id} transferido/devuelto - redirigiendo`);
					toast.info("El ticket ha sido transferido a otro usuario.", {
						autoClose: 3000
					});
					
					// Redirigir inmediatamente
					history.push("/tickets");
				} else if (wasClosedByOtherUser) {
					console.log(`🔒 TicketsCustom - Ticket ${currentTicket.id} cerrado - redirigiendo`);
					toast.info("El ticket ha sido cerrado.", {
						autoClose: 3000
					});
					
					// Redirigir inmediatamente
					history.push("/tickets");
				}
			}
		};

		socket.on(`company:ticket`, handleTicketUpdate);

		return () => {
			socket.off(`company:ticket`, handleTicketUpdate);
		};
	}, [ticketId, user.id, history]);

	return (
		<div className={classes.chatContainer}>
			<div className={classes.chatPapper}>
				<Grid container spacing={0}>
					<Grid 
						item 
						xl={4} 
						lg={5} 
						md={4}
						className={classes.contactsWrapper}
					>
						<TicketsManager />
					</Grid>
					<Grid 
						item 
						xl={8} 
						lg={7} 
						md={8}
						className={classes.messagesWrapper}
					>
						{ticketId ? (
							<Ticket />
						) : (
							<Paper square variant="outlined" className={classes.welcomeMsg}>
								<span>{i18n.t("chat.noTicketMessage")}</span>
							</Paper>
						)}
					</Grid>
				</Grid>
			</div>
		</div>
	);
};

export default TicketsCustom;
